import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { LinkEditDialog } from '@/components/settings/LinkEditDialog'
import { PronunciationEditDialog } from '@/components/settings/PronunciationEditDialog'
import { SyncListingsDropdown } from '@/components/settings/SyncListingsDropdown'
import { ListingCSVUploadDialog } from '@/components/csv-upload/ListingCSVUploadDialog'
import { KnowledgeBasesTab } from '@/components/settings/KnowledgeBasesTab'
import { PronunciationGuidesTab } from '@/components/settings/PronunciationGuidesTab'
import { LinksTab } from '@/components/settings/LinksTab'
import { ListingsTab } from '@/components/settings/ListingsTab'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { Link2, Check, Users, User } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import {
  knowledgeApiClient,
  integrationsClient,
  propertiesApiClient,
  companiesClient,
} from '@/lib/api'
import type {
  KnowledgeBase,
  KnowledgeBaseWithLinks,
  Link,
  Listing,
  Pronunciation,
  Property,
  ListingUrlSyncConfig,
} from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  extractUrls,
  getIntegrationSyncConfig,
  RelationshipAccessLevel,
  type IntegrationType,
} from '@super/shared-types'
import {
  SearchDropdown,
  SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { PropertyFilterField } from '@/components/filters/PropertyFilterField'
import type { RelationshipType } from '@/lib/api/knowledge.client'

const knowledgeBaseSchema = z.object({
  title: z.string().min(1, 'Title is required').max(500, 'Title too long'),
  body: z.string().min(1, 'Content is required'),
  propertyIds: z.array(z.string()).optional(),
  propertyGroupIds: z.array(z.string()).optional(),
  relationshipTypes: z.array(z.nativeEnum(RelationshipAccessLevel)).optional(),
})

type KnowledgeBaseFormValues = z.infer<typeof knowledgeBaseSchema>

type TabValue =
  | 'knowledge-bases'
  | 'pronunciation-guides'
  | 'links'
  | 'listings'

const tabConfig: Record<TabValue, { label: string; buttonText?: string }> = {
  'knowledge-bases': {
    label: 'Knowledge bases',
    buttonText: 'Create knowledge base',
  },
  links: { label: 'Links' },
  'pronunciation-guides': {
    label: 'Pronunciation',
    buttonText: 'Add pronunciation',
  },
  listings: {
    label: 'Listings',
    buttonText: 'Add listings website',
  },
}

export function AgentsKnowledgePage() {
  useDocumentHead({
    title: 'Knowledge Settings',
    description:
      'Manage your company knowledge bases, pronunciation guides, and links',
  })

  const { currentCompany, isSuperAdmin, refetchCurrentCompany } = useAppAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabValue>('knowledge-bases')
  const [knowledgeBases, setKnowledgeBases] = useState<
    KnowledgeBaseWithLinks[]
  >([])
  const [links, setLinks] = useState<Link[]>([])
  const [pronunciations, setPronunciations] = useState<Pronunciation[]>([])
  const [loading, setLoading] = useState(true)
  const [linksLoading, setLinksLoading] = useState(true)
  const [pronunciationsLoading, setPronunciationsLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [editingKb, setEditingKb] = useState<KnowledgeBase | null>(null)
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const [editingLink, setEditingLink] = useState<Link | null>(null)
  const [showPronunciationDialog, setShowPronunciationDialog] = useState(false)
  const [editingPronunciation, setEditingPronunciation] =
    useState<Pronunciation | null>(null)
  const [listings, setListings] = useState<Listing[]>([])
  const [listingsLoading, setListingsLoading] = useState(false)
  const [isPollingListings, setIsPollingListings] = useState(false)
  const [listingUrlSyncConfig, setListingUrlSyncConfig] =
    useState<ListingUrlSyncConfig | null>(null)
  // The listings fallback link lives on the company object (Twenty CRM), so it
  // is read straight from currentCompany rather than a dedicated endpoint.
  const [listingsFallbackLink, setListingsFallbackLink] = useState<
    string | null
  >(currentCompany?.listingsFallbackLink ?? null)
  const [highlightedLinkId, setHighlightedLinkId] = useState<string | null>(
    null,
  )
  // Connected integrations that support listings sync, split by whether their
  // recurring LISTINGS sync job is currently running. `active` → offer Stop;
  // `inactive` (connected but sync stopped) → offer Start.
  const [activeListingsIntegrations, setActiveListingsIntegrations] = useState<
    string[]
  >([])
  const [inactiveListingsIntegrations, setInactiveListingsIntegrations] =
    useState<string[]>([])
  const [showListingCsvDialog, setShowListingCsvDialog] = useState(false)
  const [listingCsvFile, setListingCsvFile] = useState<File | null>(null)
  const isSyncingListings = false // Managed by SyncListingsDropdown internally
  const [properties, setProperties] = useState<Property[]>([])
  const [selectedRelationshipTypes, setSelectedRelationshipTypes] = useState<
    SearchDropdownItem<RelationshipType | null>[]
  >([])

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const previousListingsCountRef = useRef<number>(0)

  const form = useForm<KnowledgeBaseFormValues>({
    resolver: zodResolver(knowledgeBaseSchema),
    defaultValues: {
      title: '',
      body: '',
      propertyIds: [],
      propertyGroupIds: [],
      relationshipTypes: [],
    },
  })

  // Handle tab and create URL parameters on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    const createParam = searchParams.get('create')

    if (tabParam && Object.keys(tabConfig).includes(tabParam)) {
      setActiveTab(tabParam as TabValue)
    }

    if (createParam === 'true') {
      setActiveTab('knowledge-bases')
      setEditingKb(null)
      form.reset({
        title: '',
        body: '',
        propertyIds: [],
        propertyGroupIds: [],
        relationshipTypes: [],
      })
      setSelectedRelationshipTypes([allGroupsItem])
      setShowDialog(true)
    }
  }, [])

  // Load knowledge bases, links, pronunciations, listings integration status, and properties
  useEffect(() => {
    if (currentCompany?.id) {
      loadKnowledgeBases()
      loadLinks()
      loadPronunciations()
      loadListings()
      loadListingsIntegrationStatus()
      loadListingUrlSyncStatus()
      loadProperties()
    }
  }, [currentCompany?.id, isSuperAdmin])

  // Keep the fallback link in sync with the company object (source of truth).
  useEffect(() => {
    setListingsFallbackLink(currentCompany?.listingsFallbackLink ?? null)
  }, [currentCompany?.id, currentCompany?.listingsFallbackLink])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [])

  const loadKnowledgeBases = async () => {
    if (!currentCompany?.id) return

    try {
      setLoading(true)
      const data = isSuperAdmin
        ? await knowledgeApiClient.getKnowledgeBasesForCompany(
            currentCompany.id,
          )
        : await knowledgeApiClient.getKnowledgeBases()
      setKnowledgeBases(data)
    } catch (error) {
      console.error('Error loading knowledge bases:', error)
      toast.error('Failed to load knowledge bases')
    } finally {
      setLoading(false)
    }
  }

  const loadLinks = async () => {
    if (!currentCompany?.id) return

    try {
      setLinksLoading(true)
      const data = isSuperAdmin
        ? await knowledgeApiClient.getAdminLinksForCompany(currentCompany.id)
        : await knowledgeApiClient.getLinks()
      setLinks(data)
    } catch (error) {
      console.error('Error loading links:', error)
      toast.error('Failed to load links')
    } finally {
      setLinksLoading(false)
    }
  }

  const loadPronunciations = async () => {
    if (!currentCompany?.id) return

    try {
      setPronunciationsLoading(true)
      const data = isSuperAdmin
        ? await knowledgeApiClient.getAdminPronunciationsForCompany(
            currentCompany.id,
          )
        : await knowledgeApiClient.getPronunciations()
      setPronunciations(data)
    } catch (error) {
      console.error('Error loading pronunciations:', error)
      toast.error('Failed to load pronunciations')
    } finally {
      setPronunciationsLoading(false)
    }
  }

  const loadListings = async () => {
    if (!currentCompany?.id) return

    try {
      setListingsLoading(true)
      const data = isSuperAdmin
        ? await knowledgeApiClient.getAdminListingsForCompany(currentCompany.id)
        : await knowledgeApiClient.getListings()
      setListings(data)

      // Check if listings changed while polling
      if (isPollingListings) {
        const currentCount = data.length
        const previousCount = previousListingsCountRef.current

        // Stop polling if listings are different from previous poll
        // Continue if they are the same OR if listings are empty
        if (currentCount !== previousCount && currentCount > 0) {
          toast.success('New listings detected!', {
            description: `${Math.abs(currentCount - previousCount)} listings ${currentCount > previousCount ? 'added' : 'changed'}`,
          })
          stopPolling()
        }
      }

      previousListingsCountRef.current = data.length
    } catch (error) {
      console.error('Error loading listings:', error)
      toast.error('Failed to load listings')
    } finally {
      setListingsLoading(false)
    }
  }

  const loadListingsIntegrationStatus = async () => {
    if (!currentCompany?.id) return

    try {
      const integrations = isSuperAdmin
        ? await integrationsClient.getAllAdminIntegrationsStatus(
            currentCompany.id,
          )
        : await integrationsClient.getAllIntegrationsStatus()

      // Connected integrations that support listings sync.
      const listingsIntegrations = integrations.filter(integration => {
        if (!integration.connected) return false

        const syncConfig = getIntegrationSyncConfig(
          integration.type as IntegrationType,
        )
        return (
          syncConfig?.supportsSync && syncConfig.syncTypes.includes('LISTINGS')
        )
      })

      // Split by whether the LISTINGS sync job is actually running. Treat a
      // missing flag as active so integrations connected before this field
      // existed keep their previous behavior.
      setActiveListingsIntegrations(
        listingsIntegrations
          .filter(i => i.listingSyncActive !== false)
          .map(i => i.type),
      )
      setInactiveListingsIntegrations(
        listingsIntegrations
          .filter(i => i.listingSyncActive === false)
          .map(i => i.type),
      )
    } catch (error) {
      console.error('Error loading listings integration status:', error)
    }
  }

  const loadListingUrlSyncStatus = async () => {
    if (!currentCompany?.id) return

    try {
      const config = isSuperAdmin
        ? await knowledgeApiClient.getAdminListingUrlSyncStatus(
            currentCompany.id,
          )
        : await knowledgeApiClient.getListingUrlSyncStatus()
      setListingUrlSyncConfig(config)
    } catch (error) {
      console.error('Error loading listing URL sync status:', error)
    }
  }

  const handleFallbackLinkSave = async (link: string | null) => {
    if (!currentCompany?.id) return

    try {
      const saved = isSuperAdmin
        ? await companiesClient.setAdminListingsFallbackLink(
            currentCompany.id,
            link,
          )
        : await companiesClient.setListingsFallbackLink(currentCompany.id, link)
      // `saved` is the authoritative stored value (normalized server-side) — use
      // it for the instant UI update.
      setListingsFallbackLink(saved)
      // Also refresh the shared company object so its listingsFallbackLink isn't
      // left stale: the control re-initializes from currentCompany, so without
      // this a remount (leave the tab and come back) would show the old value.
      // The refetch now returns the field correctly since the company read
      // mappings include it.
      void refetchCurrentCompany()
      toast.success(saved ? 'Fallback link updated' : 'Fallback link removed')
    } catch (error) {
      console.error('Error saving listings fallback link:', error)
      toast.error('Failed to save fallback link')
      throw error
    }
  }

  const loadProperties = async () => {
    if (!currentCompany?.id) return

    try {
      const data = isSuperAdmin
        ? await propertiesApiClient.getAllAdminPropertiesForCompany(
            currentCompany.id,
          )
        : await propertiesApiClient.getProperties()
      setProperties(data)
    } catch (error) {
      console.error('Error loading properties:', error)
      toast.error('Failed to load properties')
    }
  }

  const startPolling = () => {
    // Clear any existing polling
    stopPolling()

    // Store current listings count
    previousListingsCountRef.current = listings.length

    setIsPollingListings(true)

    // Poll every 10 seconds
    pollingIntervalRef.current = setInterval(() => {
      loadListings()
    }, 10000)

    // Stop polling after 5 minutes (300 seconds)
    pollingTimeoutRef.current = setTimeout(() => {
      stopPolling()
      toast.info('Listing sync polling stopped', {
        description: 'Maximum polling time reached (5 minutes)',
      })
    }, 300000)
  }

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
    setIsPollingListings(false)
  }

  const handleCreate = () => {
    // Note: the 'listings' tab has no create button — it uses the
    // SyncListingsDropdown instead, so handleCreate is never called for it.
    if (activeTab === 'pronunciation-guides') {
      setEditingPronunciation(null)
      setShowPronunciationDialog(true)
      return
    }

    setEditingKb(null)
    form.reset({
      title: '',
      body: '',
      propertyIds: [],
      propertyGroupIds: [],
      relationshipTypes: [],
    })
    setSelectedRelationshipTypes([allGroupsItem])
    setShowDialog(true)
  }

  const handlePronunciationEdit = (pronunciation: Pronunciation) => {
    setEditingPronunciation(pronunciation)
    setShowPronunciationDialog(true)
  }

  const handleLinkClick = (link: Link) => {
    // Navigate to links tab and highlight the link (called from knowledge base tab)
    setActiveTab('links')
    setHighlightedLinkId(link.id || null)

    // Clear highlight after 1 second (one flash)
    setTimeout(() => {
      setHighlightedLinkId(null)
    }, 1000)
  }

  const handleLinkEdit = (link: Link) => {
    // Open edit dialog (called from links tab edit button)
    setEditingLink(link)
    setShowLinkDialog(true)
  }

  const handleEdit = (kb: KnowledgeBaseWithLinks) => {
    setEditingKb(kb)
    form.reset({
      title: kb.title,
      body: kb.body,
      propertyIds: kb.properties?.map(p => p.propertyId) || [],
      propertyGroupIds: kb.propertyGroups?.map(g => g.propertyGroupId) || [],
    })

    // Pre-populate relationship types
    const relationshipTypes =
      kb.relationshipTypes?.map(rt => rt.relationshipType) || []
    if (relationshipTypes.length > 0) {
      const typeItems = relationshipTypes
        .map(type =>
          relationshipTypeDropdownItems.find(item => item.id === type),
        )
        .filter(Boolean) as SearchDropdownItem<RelationshipType | null>[]
      setSelectedRelationshipTypes(typeItems)
      form.setValue(
        'relationshipTypes',
        relationshipTypes as RelationshipAccessLevel[],
      )
    } else {
      // No relationship types = all groups
      setSelectedRelationshipTypes([allGroupsItem])
      form.setValue('relationshipTypes', [])
    }

    setShowDialog(true)
  }

  const allGroupsItem: SearchDropdownItem<RelationshipType | null> = {
    id: 'all-groups',
    label: 'All contact types',
    sublabel: 'This knowledge base applies to all contact types',
    icon: <Users className="h-4 w-4" />,
    data: null,
    isExclusive: true,
  }

  const relationshipTypeDropdownItems: SearchDropdownItem<RelationshipType | null>[] =
    [
      allGroupsItem,
      {
        id: 'OWNER',
        label: 'Owner',
        sublabel: 'Property owners',
        icon: <User className="h-4 w-4" />,
        data: 'OWNER',
      },
      {
        id: 'TENANT',
        label: 'Tenant',
        sublabel: 'Current tenants/renters',
        icon: <User className="h-4 w-4" />,
        data: 'TENANT',
      },
      {
        id: 'MANAGER',
        label: 'Manager',
        sublabel: 'Property managers',
        icon: <User className="h-4 w-4" />,
        data: 'MANAGER',
      },
      {
        id: 'VENDOR',
        label: 'Vendor',
        sublabel: 'Vendors and contractors',
        icon: <User className="h-4 w-4" />,
        data: 'VENDOR',
      },
    ]

  const handleRelationshipTypeSelect = (
    items:
      | SearchDropdownItem<RelationshipType | null>[]
      | SearchDropdownItem<RelationshipType | null>
      | null,
  ) => {
    const typeItems = Array.isArray(items) ? items : items ? [items] : []

    const hasAllGroups = typeItems.some(item => item.id === 'all-groups')

    if (hasAllGroups) {
      setSelectedRelationshipTypes([allGroupsItem])
      form.setValue('relationshipTypes', []) // Empty = all groups (company-wide)
    } else {
      setSelectedRelationshipTypes(typeItems)
      form.setValue(
        'relationshipTypes',
        typeItems.map(item => item.id) as RelationshipAccessLevel[],
      )
    }
  }

  const onSubmit = async (data: KnowledgeBaseFormValues) => {
    if (!currentCompany?.id) return

    try {
      setSaving(true)

      if (editingKb) {
        // Update existing
        if (isSuperAdmin) {
          await knowledgeApiClient.updateKnowledgeBaseForCompany(
            editingKb.id,
            currentCompany.id,
            data,
          )
        } else {
          await knowledgeApiClient.updateKnowledgeBase(editingKb.id, data)
        }
        toast.success('Knowledge base updated successfully')
      } else {
        // Create new
        if (isSuperAdmin) {
          await knowledgeApiClient.createKnowledgeBaseForCompany(
            currentCompany.id,
            data,
          )
        } else {
          await knowledgeApiClient.createKnowledgeBase(data)
        }
        toast.success('Knowledge base created successfully')
      }

      setShowDialog(false)
      loadKnowledgeBases()
      loadLinks() // Reload links as they may have been updated with new URLs
    } catch (error) {
      console.error('Error saving knowledge base:', error)
      toast.error('Failed to save knowledge base')
    } finally {
      setSaving(false)
    }
  }

  const handleTabChange = (value: string) => {
    const newTab = value as TabValue
    setActiveTab(newTab)
    // Update URL with tab parameter
    setSearchParams({ tab: newTab })
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24 mt-6">
        <PageHeader title="Manage your company knowledge and resources" />

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="space-y-4 lg:space-y-0">
            <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
              <TabsList
                className="grid grid-cols-4 mb-4 lg:mb-0"
                dropdownBreakpoint="lg"
              >
                <TabsTrigger value="knowledge-bases">
                  {tabConfig['knowledge-bases'].label}
                </TabsTrigger>
                <TabsTrigger value="links">
                  {tabConfig['links'].label}
                </TabsTrigger>
                <TabsTrigger value="pronunciation-guides">
                  {tabConfig['pronunciation-guides'].label}
                </TabsTrigger>
                <TabsTrigger value="listings">
                  <div className="flex items-center space-x-2">
                    <span>{tabConfig['listings'].label}</span>
                    {isPollingListings && (
                      <span
                        className="h-2 w-2 rounded-full bg-success animate-pulse"
                        title="Polling for new listings"
                      />
                    )}
                  </div>
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center space-x-2">
                {activeTab === 'listings' && (
                  <>
                    {/* Upload CSV is the secondary action; Sync Listings is
                        the primary. Avoid two side-by-side primary buttons. */}
                    <Button
                      variant="outline"
                      onClick={() => setShowListingCsvDialog(true)}
                    >
                      Upload CSV
                    </Button>
                    <SyncListingsDropdown
                      syncConfig={listingUrlSyncConfig}
                      activeListingsIntegration={activeListingsIntegrations}
                      inactiveListingsIntegration={inactiveListingsIntegrations}
                      onSyncConfigChange={() => {
                        loadListingUrlSyncStatus()
                        loadListingsIntegrationStatus()
                      }}
                      isSyncing={isSyncingListings}
                      onManualSyncStart={startPolling}
                    />
                  </>
                )}
                {activeTab !== 'listings' &&
                  tabConfig[activeTab].buttonText && (
                    <Button onClick={handleCreate} variant="form">
                      {tabConfig[activeTab].buttonText}
                    </Button>
                  )}
              </div>
            </div>
          </div>

          <TabsContent value="knowledge-bases" className="mt-6">
            <KnowledgeBasesTab
              knowledgeBases={knowledgeBases}
              loading={loading}
              properties={properties}
              onEdit={handleEdit}
              onLinkEdit={handleLinkClick}
              onReload={loadKnowledgeBases}
            />
          </TabsContent>

          <TabsContent value="pronunciation-guides" className="mt-6">
            <PronunciationGuidesTab
              pronunciations={pronunciations}
              loading={pronunciationsLoading}
              onEdit={handlePronunciationEdit}
              onReload={loadPronunciations}
            />
          </TabsContent>

          <TabsContent value="links" className="mt-6">
            <LinksTab
              links={links}
              loading={linksLoading}
              onLinkEdit={handleLinkEdit}
              highlightedLinkId={highlightedLinkId}
              onNavigateToPronunciation={() =>
                setActiveTab('pronunciation-guides')
              }
            />
          </TabsContent>

          <TabsContent value="listings" className="mt-6">
            <ListingsTab
              listings={listings}
              loading={listingsLoading || isSyncingListings}
              syncConfig={listingUrlSyncConfig}
              fallbackLink={listingsFallbackLink}
              fallbackLinkLoading={!currentCompany}
              onFallbackLinkSave={handleFallbackLinkSave}
            />
          </TabsContent>
        </Tabs>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent fullWidth fullHeight>
            <div className="mx-auto w-full max-w-5xl h-full flex flex-col">
              <DialogHeader>
                <DialogTitle>
                  {editingKb ? 'Edit knowledge base' : 'Create knowledge base'}
                </DialogTitle>
              </DialogHeader>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4 flex-1 flex flex-col"
                >
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter knowledge base title"
                            className="max-w-md"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <PropertyFilterField
                    name="propertyIds"
                    groupsName="propertyGroupIds"
                    allSublabel="This knowledge base applies to all properties"
                    buttonClassName="w-full max-w-lg"
                  />

                  <FormField
                    control={form.control}
                    name="relationshipTypes"
                    render={() => (
                      <FormItem>
                        <FormLabel>Contact types</FormLabel>
                        <FormControl>
                          <SearchDropdown<RelationshipType | null>
                            items={relationshipTypeDropdownItems}
                            selectedItems={selectedRelationshipTypes}
                            onSelect={handleRelationshipTypeSelect}
                            multiSelect={true}
                            placeholder="All contact types (company-wide knowledge)"
                            searchPlaceholder="Search contact types..."
                            buttonClassName="w-full max-w-lg"
                            emptyMessage="No contact types found"
                            renderItem={(item, isSelected) => (
                              <>
                                {item.icon}
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium truncate">
                                    {item.label}
                                  </div>
                                  {item.sublabel && (
                                    <div className="text-xs text-muted-foreground truncate mt-1">
                                      {item.sublabel}
                                    </div>
                                  )}
                                </div>
                                {isSelected && (
                                  <Check className="h-4 w-4 shrink-0" />
                                )}
                              </>
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="body"
                    render={({ field }) => (
                      <FormItem className="flex-1 flex flex-col">
                        <FormLabel>Content</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter your business rules, procedures, and information. URLs will be automatically detected and extracted."
                            className="flex-1 min-h-0 resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch('body') &&
                    extractUrls(form.watch('body')).length > 0 && (
                      <div>
                        <Separator className="my-4" />
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">
                            Detected Links:
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {extractUrls(form.watch('body')).map(
                              (url: string, index: number) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  className="text-xs"
                                >
                                  <Link2 className="h-3 w-3 mr-1" />
                                  {url}
                                </Badge>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowDialog(false)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving}>
                      {saving
                        ? 'Saving...'
                        : editingKb
                          ? 'Update knowledge base'
                          : 'Create knowledge base'}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </div>
          </DialogContent>
        </Dialog>

        <LinkEditDialog
          open={showLinkDialog}
          onOpenChange={setShowLinkDialog}
          link={editingLink}
          onSave={() => {
            loadKnowledgeBases()
            loadLinks()
            loadPronunciations()
          }}
        />

        <PronunciationEditDialog
          open={showPronunciationDialog}
          onOpenChange={setShowPronunciationDialog}
          pronunciation={editingPronunciation}
          onSave={loadPronunciations}
          mode={editingPronunciation ? 'edit' : 'create'}
        />

        <ListingCSVUploadDialog
          open={showListingCsvDialog}
          onOpenChange={setShowListingCsvDialog}
          preloadedFile={listingCsvFile}
          onFileChange={setListingCsvFile}
          activeListingsIntegrations={activeListingsIntegrations}
          activeUrlSync={listingUrlSyncConfig !== null}
          onUploadComplete={() => {
            loadListings()
            loadListingUrlSyncStatus()
            loadListingsIntegrationStatus()
          }}
        />
      </div>
    </div>
  )
}
